/**
 * Floating chat widget — brand-compliant per FinminOS Design System.
 *
 * The widget opens to a short menu where the customer picks one of two paths:
 *
 *   1. "Ask a tax question"  → the existing tax knowledge-base RAG flow
 *      (POST /ask via askChat). Long-running (~20-30s); typing dots stay up,
 *      a soft hint appears after 8s, no client-side timeout.
 *
 *   2. "Report an issue"     → a bot-driven support intake. The bot walks the
 *      customer through a couple of questions (category → details → confirm)
 *      and raises a ticket via createSupportTicket. The intake is scripted
 *      client-side for now; the backend ticket endpoint is the durable store.
 *
 * Conversation turns are kept in `messages` as a plain transcript; the live
 * choices (menu buttons, category chips, confirm buttons) are rendered from
 * widget state in the footer, not persisted as messages.
 *
 * Visual language (all via design tokens — no hard-coded hex):
 *   - Launcher:   Amber (bg-accent) CTA
 *   - Panel:      Cream (bg-background) with soft 16px radius
 *   - Header:     Forest (bg-primary) with Cream text, Optima display title
 *   - Bubbles:    Customer = Forest on Cream;  Assistant = Mint on Forest
 *   - Composer:   hairline border, Amber send button
 *   - No emoji, ALL CAPS button labels with wide tracking
 */
import * as React from 'react';
import {
  BrandLogomark,
  Button,
  Input,
  MarkdownRenderer,
  cn,
} from '@finmin/ui';
import { useAuth } from '@finmin/auth';
import { ChevronLeft, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { askChat } from '@/api/chat';
import {
  categoryLabel,
  createSupportTicket,
  SUPPORT_CATEGORIES,
  supportErrorMessage,
  type SupportCategoryId,
} from '@/api/support';
import type { ChatMessage } from './types';

const STORAGE_PREFIX = 'finmin.chat.widget.';
/** Show "still looking..." hint after this long. The API itself isn't capped. */
const LONG_REPLY_HINT_MS = 8_000;

/** Which path the conversation is currently on. */
type Mode = 'menu' | 'tax' | 'support';
/** Steps of the bot-driven support intake (only meaningful when mode==='support'). */
type SupportStep = 'category' | 'description' | 'confirm' | 'done';

interface SupportDraft {
  category?: SupportCategoryId;
  description?: string;
}

function welcomeMessage(): ChatMessage {
  return {
    id: uid(),
    sender: 'assistant',
    text:
      "Hi — I'm Finny💫. How can I help today? You can ask me a tax question, or report an issue with the platform and I'll raise a ticket for you.",
    at: new Date().toISOString(),
  };
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function ChatWidget() {
  const { user } = useAuth();
  const storageKey = user?.id ? `${STORAGE_PREFIX}${user.id}` : null;

  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [draft, setDraft] = React.useState('');
  const [isThinking, setIsThinking] = React.useState(false);
  const [longReplyHint, setLongReplyHint] = React.useState(false);
  const [mode, setMode] = React.useState<Mode>('menu');
  const [supportStep, setSupportStep] = React.useState<SupportStep>('category');
  const [supportDraft, setSupportDraft] = React.useState<SupportDraft>({});
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        setMessages(Array.isArray(parsed) ? parsed : [welcomeMessage()]);
      } else {
        setMessages([welcomeMessage()]);
      }
    } catch {
      setMessages([welcomeMessage()]);
    }
    // A returning customer always lands back on the menu; the transcript above
    // stays as history. Mode/step are transient and intentionally not persisted.
    setMode('menu');
    setSupportDraft({});
  }, [storageKey]);

  React.useEffect(() => {
    if (!storageKey || messages.length === 0) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {
      // non-fatal
    }
  }, [storageKey, messages]);

  React.useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const unreadHint =
    !open && messages.some((m) => m.sender === 'assistant') && messages.length > 0;

  // ── transcript helpers ────────────────────────────────────────────────────

  const pushAssistant = React.useCallback(
    (text: string, extra?: Partial<ChatMessage>) =>
      setMessages((m) => [
        ...m,
        { id: uid(), sender: 'assistant', text, at: new Date().toISOString(), ...extra },
      ]),
    [],
  );

  const pushCustomer = React.useCallback(
    (text: string) =>
      setMessages((m) => [
        ...m,
        { id: uid(), sender: 'customer', text, at: new Date().toISOString() },
      ]),
    [],
  );

  function resetToMenu() {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsThinking(false);
    setLongReplyHint(false);
    setDraft('');
    setSupportDraft({});
    setMode('menu');
  }

  // ── menu choices ──────────────────────────────────────────────────────────

  function chooseTax() {
    setMode('tax');
    pushCustomer('Ask a tax question');
    pushAssistant(
      'Sure — ask me anything about Indian income tax: capital gains, exemptions, deductions, filing.',
    );
  }

  function chooseSupport() {
    setSupportDraft({});
    setSupportStep('category');
    setMode('support');
    pushCustomer('Report an issue or concern');
    pushAssistant("I'm sorry something's not right. What is this about?");
  }

  // ── support intake steps ──────────────────────────────────────────────────

  function pickCategory(id: SupportCategoryId) {
    setSupportDraft((d) => ({ ...d, category: id }));
    setSupportStep('description');
    pushCustomer(categoryLabel(id));
    pushAssistant(
      'Got it. Please describe what happened — include any details that help, like dates, amounts, or what you expected versus what you saw.',
    );
  }

  function submitDescription(text: string) {
    setSupportDraft((d) => ({ ...d, description: text }));
    setSupportStep('confirm');
    pushCustomer(text);
    const cat = supportDraft.category ? categoryLabel(supportDraft.category) : '—';
    pushAssistant(
      `Here's what I'll raise:\n\n**Category:** ${cat}\n\n**Details:** ${text}\n\nShall I create this ticket?`,
    );
  }

  async function confirmTicket() {
    const { category, description } = supportDraft;
    if (!category || !description || isThinking) return;

    setIsThinking(true);
    try {
      const ticket = await createSupportTicket({ category, description });
      setSupportStep('done');
      pushAssistant(
        `Done — I've raised your ticket. Your reference is **${ticket.reference}**. ` +
          'Our team will look into it and follow up with you. Is there anything else I can help with?',
      );
    } catch (err) {
      // Stay on 'confirm' so the customer can retry without re-entering details.
      pushAssistant(supportErrorMessage(err), { isError: true });
    } finally {
      setIsThinking(false);
    }
  }

  // ── tax question ──────────────────────────────────────────────────────────

  async function sendTaxQuestion(text: string) {
    pushCustomer(text);
    setDraft('');
    setIsThinking(true);
    setLongReplyHint(false);

    const longTimer = window.setTimeout(
      () => setLongReplyHint(true),
      LONG_REPLY_HINT_MS,
    );

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const res = await askChat(text, { topK: 5, signal: ac.signal });
      pushAssistant(res.answer, { citations: res.citations });
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong reaching the assistant.';
      pushAssistant(`_${message}_`, { isError: true });
    } finally {
      window.clearTimeout(longTimer);
      setIsThinking(false);
      setLongReplyHint(false);
      if (abortRef.current === ac) abortRef.current = null;
    }
  }

  // ── composer dispatch ─────────────────────────────────────────────────────

  /** Composer is only visible when free-text input is expected. */
  const showComposer =
    mode === 'tax' || (mode === 'support' && supportStep === 'description');

  function send() {
    const text = draft.trim();
    if (!text || isThinking) return;
    if (mode === 'tax') {
      void sendTaxQuestion(text);
    } else if (mode === 'support' && supportStep === 'description') {
      setDraft('');
      submitDescription(text);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!user) return null;

  return (
    <>
      {/* ── Launcher ─────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Close chat' : 'Open chat'}
        className={cn(
          'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full',
          'bg-accent text-accent-foreground shadow-md',
          'transition-all duration-200 hover:shadow-lg hover:scale-105',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        {open ? (
          <X className="h-5 w-5" aria-hidden />
        ) : (
          <MessageCircle className="h-5 w-5" aria-hidden />
        )}
        {unreadHint && !open && (
          <span
            className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background"
            aria-hidden
          />
        )}
      </button>

      {/* ── Panel ────────────────────────────────────────────────────────── */}
      <div
        className={cn(
          'fixed bottom-24 right-6 z-50 origin-bottom-right',
          'w-[calc(100vw-3rem)] max-w-[420px] h-[min(620px,calc(100vh-8rem))]',
          'rounded-2xl border border-border bg-background shadow-xl',
          'flex flex-col overflow-hidden',
          'transition-[opacity,transform] duration-200',
          open
            ? 'opacity-100 scale-100 pointer-events-auto'
            : 'opacity-0 scale-95 pointer-events-none',
        )}
        role="dialog"
        aria-modal="false"
        aria-label="Finny"
        aria-hidden={!open}
      >
        <Header
          onClose={() => setOpen(false)}
          onBack={mode === 'menu' ? undefined : resetToMenu}
        />
        <MessageList
          messages={messages}
          isThinking={isThinking}
          longReplyHint={longReplyHint}
        />
        {showComposer ? (
          <Composer
            value={draft}
            onChange={setDraft}
            onSend={send}
            onKeyDown={onKeyDown}
            disabled={isThinking}
            placeholder={
              mode === 'support'
                ? 'Describe the issue…'
                : isThinking
                  ? 'Thinking…'
                  : 'Ask a tax question…'
            }
            showDisclaimer={mode === 'tax'}
          />
        ) : (
          <ActionDock
            mode={mode}
            supportStep={supportStep}
            disabled={isThinking}
            onChooseTax={chooseTax}
            onChooseSupport={chooseSupport}
            onPickCategory={pickCategory}
            onConfirmTicket={() => void confirmTicket()}
            onStartOver={resetToMenu}
          />
        )}
      </div>
    </>
  );
}

// ── Footer: live choices (menu / category chips / confirm) ──────────────────

function ActionDock({
  mode,
  supportStep,
  disabled,
  onChooseTax,
  onChooseSupport,
  onPickCategory,
  onConfirmTicket,
  onStartOver,
}: {
  mode: Mode;
  supportStep: SupportStep;
  disabled: boolean;
  onChooseTax: () => void;
  onChooseSupport: () => void;
  onPickCategory: (id: SupportCategoryId) => void;
  onConfirmTicket: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="border-t border-border bg-background p-3">
      {mode === 'menu' && (
        <div className="flex flex-col gap-2">
          <DockButton variant="primary" onClick={onChooseTax}>
            Ask a tax question
          </DockButton>
          <DockButton variant="outline" onClick={onChooseSupport}>
            Report an issue or concern
          </DockButton>
        </div>
      )}

      {mode === 'support' && supportStep === 'category' && (
        <div className="flex flex-wrap gap-2">
          {SUPPORT_CATEGORIES.map((c) => (
            <Chip key={c.id} onClick={() => onPickCategory(c.id)}>
              {c.label}
            </Chip>
          ))}
        </div>
      )}

      {mode === 'support' && supportStep === 'confirm' && (
        <div className="flex items-center gap-2">
          <DockButton
            variant="primary"
            onClick={onConfirmTicket}
            disabled={disabled}
            className="flex-1"
          >
            {disabled ? 'Raising…' : 'Raise ticket'}
          </DockButton>
          <DockButton variant="outline" onClick={onStartOver} disabled={disabled}>
            Start over
          </DockButton>
        </div>
      )}

      {mode === 'support' && supportStep === 'done' && (
        <div className="flex flex-col gap-2">
          <DockButton variant="outline" onClick={onStartOver}>
            Back to menu
          </DockButton>
        </div>
      )}
    </div>
  );
}

function DockButton({
  variant,
  onClick,
  disabled,
  className,
  children,
}: {
  variant: 'primary' | 'outline';
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-md px-3.5 py-2.5 text-sm font-medium uppercase tracking-wider',
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variant === 'primary'
          ? 'bg-accent text-accent-foreground hover:bg-accent/90'
          : 'border border-border bg-background text-foreground hover:bg-muted',
        className,
      )}
    >
      {children}
    </button>
  );
}

function Chip({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground',
        'transition-colors hover:bg-muted',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
      )}
    >
      {children}
    </button>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Header({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack?: () => void;
}) {
  return (
    <header className="flex items-center gap-3 bg-primary px-4 py-3 text-primary-foreground">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to menu"
          className="-ml-1 rounded-md p-1.5 hover:bg-primary-foreground/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10">
          <BrandLogomark className="h-5 w-5 text-primary-foreground" />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-display text-base leading-tight">Finny💫</p>
        <p className="font-display italic text-xs text-accent leading-tight mt-0.5">
          Your tax assistant.
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close chat"
        className="rounded-md p-1.5 hover:bg-primary-foreground/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </header>
  );
}

function MessageList({
  messages,
  isThinking,
  longReplyHint,
}: {
  messages: ChatMessage[];
  isThinking: boolean;
  longReplyHint: boolean;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isThinking, longReplyHint]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
    >
      {messages.map((m) => (
        <Bubble key={m.id} message={m} />
      ))}
      {isThinking && <TypingBubble longReplyHint={longReplyHint} />}
    </div>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const isCustomer = message.sender === 'customer';
  return (
    <div className={cn('flex', isCustomer ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[88%] rounded-2xl px-3.5 py-2 text-sm',
          isCustomer
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : message.isError
              ? 'bg-destructive/10 text-destructive border border-destructive/20 rounded-bl-sm'
              : 'bg-secondary text-secondary-foreground rounded-bl-sm',
        )}
      >
        {isCustomer ? (
          <p className="whitespace-pre-wrap break-words">{message.text}</p>
        ) : (
          <MarkdownRenderer>{message.text}</MarkdownRenderer>
        )}
        {/* Citations are intentionally hidden from the UI for now; they're
         * still attached to ChatMessage so we can surface them later (e.g.
         * a "view sources" affordance) without re-plumbing the API. */}
        <p
          className={cn(
            'mt-1.5 text-[10px] tabular-nums',
            isCustomer
              ? 'text-primary-foreground/60'
              : message.isError
                ? 'text-destructive/70'
                : 'text-muted-foreground',
          )}
        >
          {formatTime(message.at)}
        </p>
      </div>
    </div>
  );
}

function TypingBubble({ longReplyHint }: { longReplyHint: boolean }) {
  return (
    <div className="flex justify-start">
      <div className="rounded-2xl rounded-bl-sm bg-secondary px-3.5 py-2.5 max-w-[88%]">
        <span className="flex gap-1" aria-label="Assistant is typing">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-secondary-foreground/50 animate-bounce"
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </span>
        {longReplyHint && (
          <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
            Looking through tax rulings — this can take 20–30 seconds.
          </p>
        )}
      </div>
    </div>
  );
}

function Composer({
  value,
  onChange,
  onSend,
  onKeyDown,
  disabled,
  placeholder,
  showDisclaimer,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  disabled: boolean;
  placeholder: string;
  showDisclaimer: boolean;
}) {
  return (
    <div className="border-t border-border bg-background p-3 space-y-2">
      <div className="flex items-end gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Message"
          disabled={disabled}
          className="flex-1"
        />
        <Button
          type="button"
          onClick={onSend}
          disabled={!value.trim() || disabled}
          size="sm"
          className="bg-accent text-accent-foreground hover:bg-accent/90 uppercase tracking-wider gap-1.5"
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
          Send
        </Button>
      </div>
      {showDisclaimer && (
        <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Sparkles className="h-2.5 w-2.5" aria-hidden />
          Not a substitute for advice on your specific case.
        </p>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
